/**
 * Login Modal Component
 * Supports Google Sign-In for web builds
 * Configurable via environment.googleSignInEnabled
 */

import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  effect,
  ViewChild,
  ElementRef,
  AfterViewInit,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalService } from '../../../core/services/modal.service';
import { AuthService } from '../../../core/services/auth.service';
import { environment } from '../../../../environments/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

declare const google: any;

@Component({
  selector: 'app-login-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <!-- Modal Backdrop -->
    <div
      *ngIf="isOpen()"
      class="fixed inset-0 backdrop-blur-md bg-black/30 z-[60] flex items-center justify-center p-4"
      style="pointer-events: auto;"
      (click)="onBackdropClick($event)"
    >
      <!-- Modal Container -->
      <div
        class="login-modal bg-white rounded-xl shadow-2xl max-w-md w-full relative"
        style="pointer-events: auto;"
        (click)="$event.stopPropagation()"
      >
        <!-- Close Button -->
        <button
          class="modal-close absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center"
          (click)="close()"
          aria-label="Cerrar"
          type="button"
        >
          <i class="fas fa-times"></i>
        </button>

        <!-- Modal Content -->
        <div class="p-6 pt-8">
          <!-- Logo -->
          <div class="flex justify-center mb-4">
            <img
              src="/gastronauta_logo_con_texto_lateral.svg"
              alt="Gastronauta"
              class="h-8 w-auto"
            />
          </div>

          <!-- Header -->
          <div class="login-header text-center mb-6">
            <h2 class="text-2xl font-bold text-gray-900 mb-2">
              {{ 'auth.login' | translate }}
            </h2>
            <p class="text-sm text-gray-600">
              {{ googleSignInEnabled ? ('auth.loginWithGoogle' | translate) : ('auth.loginNotAvailable' | translate) }}
            </p>
          </div>

          <!-- Google Sign-In (when enabled) -->
          <ng-container *ngIf="googleSignInEnabled">
            <!-- Loading state -->
            <div *ngIf="isLoading()" class="flex justify-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>

            <!-- Error message -->
            <div *ngIf="error()" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p class="text-sm text-red-800">{{ error() }}</p>
            </div>

            <!-- Google Sign-In Button Container -->
            <div *ngIf="!isLoading()" class="google-signin-container mb-4">
              <div #googleButton id="google-signin-button" class="flex justify-center"></div>
            </div>

            <!-- Divider -->
            <div class="relative my-6">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-200"></div>
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white text-gray-500">o continúa sin cuenta</span>
              </div>
            </div>
          </ng-container>

          <!-- Info Message (when Google disabled or as fallback) -->
          <div *ngIf="!googleSignInEnabled" class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center mb-6">
            <i class="fas fa-info-circle text-blue-600 text-xl mb-2"></i>
            <p class="text-sm text-blue-800">
              La aplicación funciona con sesiones anónimas. No es necesario iniciar sesión para usar todas las funcionalidades.
            </p>
          </div>

          <!-- Close/Continue Button -->
          <button
            (click)="close()"
            class="w-full bg-primary-600 text-white rounded-lg px-4 py-3 text-sm font-semibold hover:bg-primary-700 transition-colors"
            style="background-color: #e91d63"
          >
            {{ googleSignInEnabled ? ('auth.continueWithout' | translate) : ('auth.understood' | translate) }}
          </button>

          <!-- Privacy note -->
          <p *ngIf="googleSignInEnabled" class="text-xs text-gray-500 text-center mt-4">
            Al iniciar sesión, aceptas nuestros
            <a href="/privacy" class="text-primary-600 hover:underline">Términos</a>
            y
            <a href="/privacy" class="text-primary-600 hover:underline">Política de Privacidad</a>
          </p>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      .fixed.inset-0 {
        animation: fadeIn 0.3s ease-out;
      }

      .login-modal {
        animation: slideIn 0.3s ease-out;
        max-height: 90vh;
        overflow-y: auto;
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slideIn {
        from { transform: translateY(-20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      .modal-close {
        transition: all 0.2s;
      }

      .modal-close:hover i {
        transform: scale(1.2);
      }

      .google-signin-container {
        min-height: 44px;
      }

      /* Style the Google button container */
      #google-signin-button {
        display: flex;
        justify-content: center;
      }
    `,
  ],
})
export class LoginModalComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('googleButton') googleButton!: ElementRef;

  private modalService = inject(ModalService);
  private authService = inject(AuthService);
  private ngZone = inject(NgZone);
  private translate = inject(TranslateService);

  // Modal ID
  private modalId = 'login-modal';

  // Environment configuration
  googleSignInEnabled = environment.googleSignInEnabled ?? false;
  private googleClientId = environment.googleClientId;

  // Signals
  isOpen = signal(false);
  isLoading = signal(false);
  error = signal<string | null>(null);

  private googleInitialized = false;

  constructor() {
    // Watch for modal state changes
    effect(() => {
      const isModalOpen = this.modalService.isModalOpen(this.modalId);
      this.isOpen.set(isModalOpen);

      if (isModalOpen) {
        document.body.style.overflow = 'hidden';
        document.body.style.pointerEvents = 'none';
        // Initialize Google Sign-In when modal opens
        if (this.googleSignInEnabled) {
          this.initializeGoogleSignIn();
        }
      } else {
        document.body.style.overflow = '';
        document.body.style.pointerEvents = '';
      }
    });
  }

  ngOnInit(): void {
    document.addEventListener('keydown', this.handleKeyDown);
    
    // Load Google Identity Services script if enabled
    if (this.googleSignInEnabled) {
      this.loadGoogleScript();
    }
  }

  ngAfterViewInit(): void {
    // Initialize after view is ready if modal is already open
    if (this.isOpen() && this.googleSignInEnabled) {
      setTimeout(() => this.initializeGoogleSignIn(), 100);
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    document.body.style.pointerEvents = '';
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Load Google Identity Services script
   */
  private loadGoogleScript(): void {
    if (document.getElementById('google-gsi-script')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('✅ Google Identity Services script loaded');
      if (this.isOpen()) {
        this.initializeGoogleSignIn();
      }
    };
    script.onerror = () => {
      console.error('❌ Failed to load Google Identity Services script');
      this.error.set(this.translate.instant('auth.loginError'));
    };
    document.head.appendChild(script);
  }

  /**
   * Initialize Google Sign-In button
   */
  private initializeGoogleSignIn(): void {
    if (!this.googleSignInEnabled || this.googleInitialized) {
      return;
    }

    if (typeof google === 'undefined' || !google.accounts) {
      console.log('⏳ Waiting for Google SDK...');
      setTimeout(() => this.initializeGoogleSignIn(), 200);
      return;
    }

    if (!this.googleButton?.nativeElement) {
      console.log('⏳ Waiting for button container...');
      setTimeout(() => this.initializeGoogleSignIn(), 100);
      return;
    }

    try {
      console.log('🔧 Initializing Google Sign-In with client ID:', this.googleClientId?.substring(0, 20) + '...');

      google.accounts.id.initialize({
        client_id: this.googleClientId,
        callback: (response: any) => this.handleGoogleCredential(response),
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      google.accounts.id.renderButton(this.googleButton.nativeElement, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: 300,
        locale: 'es',
      });

      this.googleInitialized = true;
      console.log('✅ Google Sign-In button rendered');
    } catch (err) {
      console.error('❌ Error initializing Google Sign-In:', err);
      this.error.set(this.translate.instant('auth.loginError'));
    }
  }

  /**
   * Handle Google credential response
   */
  private handleGoogleCredential(response: any): void {
    this.ngZone.run(async () => {
      if (!response?.credential) {
        console.error('❌ No credential in Google response');
        this.error.set(this.translate.instant('auth.loginError'));
        return;
      }

      console.log('🔑 Received Google credential, exchanging with backend...');
      this.isLoading.set(true);
      this.error.set(null);

      try {
        const authResponse = await this.authService.googleLogin(response.credential);
        console.log('✅ Google authentication successful');
        this.close();
      } catch (err: any) {
        console.error('❌ Google authentication failed:', err);
        this.error.set(err?.message || this.translate.instant('auth.loginError'));
      } finally {
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Open the modal
   */
  open(): void {
    this.error.set(null);
    this.googleInitialized = false; // Reset to re-render button
    this.modalService.openModal(this.modalId);
  }

  /**
   * Close the modal
   */
  close(): void {
    this.modalService.closeModal(this.modalId);
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('fixed')) {
      this.close();
    }
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.isOpen()) {
      this.close();
    }
  };
}
